import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Wrench, Ticket, Store, Newspaper, MessageSquareWarning, Building2, MapPin, Mail, Hash } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import PageShell, { PageHead, RichText } from "@/components/pages/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { COMPANY } from "@/config/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHY_ICONS = [Wrench, Ticket, Store, Newspaper, MessageSquareWarning];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fieldCls = "bg-parchment border-line text-ink placeholder:text-ink-muted/60 rounded-xl focus-visible:ring-moss focus-visible:ring-offset-0";
const EMPTY = { name: "", email: "", message: "", website: "" };

const usePrefill = () => {
  const { t } = useLang();
  const pkg = useSearchParams()[0].get("package");
  const s = t.pages.supporter;
  if (!pkg) return "";
  if (pkg === "custom") return t.pages.contact.prefillCustom;
  const name = s.packages[pkg]?.name;
  return name ? t.pages.contact.prefill.replace("{package}", name) : t.pages.contact.prefillGeneric;
};

const ContactForm = () => {
  const { t, lang } = useLang();
  const c = t.pages.contact;
  const prefill = usePrefill();
  const [form, setForm] = useState({ ...EMPTY, message: prefill });
  const lastPrefill = useRef(prefill);
  useEffect(() => {
    // Re-sync the prefilled text on package/language change unless the visitor edited it.
    const prev = lastPrefill.current;
    lastPrefill.current = prefill;
    setForm((f) => (f.message === prev ? { ...f, message: prefill } : f));
  }, [prefill]);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !EMAIL_RE.test(form.email.trim()) || form.message.trim().length < 10) {
      setError(c.invalid);
      return;
    }
    setError("");
    setState("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setState("sent");
      setForm(EMPTY);
    } catch (err) {
      const code = err.response?.status;
      setError(code === 429 ? c.tooMany : code === 422 ? c.invalid : c.error);
      setState("idle");
    }
  };

  if (state === "sent") {
    return (
      <div data-testid="contact-success" className="flex flex-col items-start gap-4 py-10">
        <CheckCircle2 className="w-12 h-12 text-moss" />
        <h2 className="font-display text-2xl font-semibold text-ink">{c.sent}</h2>
        <p className="text-ink-muted">{c.sentBody}</p>
        <button type="button" data-testid="contact-send-another" onClick={() => setState("idle")} className="mt-2 rounded-full border border-line px-5 py-2.5 font-bold text-ink hover:border-moss hover:text-moss transition-colors">
          {c.another}
        </button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} noValidate className="space-y-5">
      <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-ink">{c.formTitle}</h2>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="contact-name" className="font-bold text-ink">{c.name}</Label>
          <Input id="contact-name" data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder={c.namePh} maxLength={100} autoComplete="name" className={`h-12 ${fieldCls}`} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-email" className="font-bold text-ink">{c.email}</Label>
          <Input id="contact-email" type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} placeholder={c.emailPh} maxLength={200} autoComplete="email" className={`h-12 ${fieldCls}`} />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-message" className="font-bold text-ink">{c.message}</Label>
        <Textarea id="contact-message" data-testid="contact-message-input" value={form.message} onChange={set("message")} placeholder={c.messagePh} maxLength={4000} rows={6} className={`min-h-[160px] ${fieldCls}`} />
      </div>
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] w-px h-px opacity-0" />
      {error && <p data-testid="contact-error" role="alert" className="rounded-xl bg-rowan/10 border border-rowan/30 px-4 py-3 text-sm font-bold text-rowan">{error}</p>}
      <div className="flex flex-wrap items-center gap-4 pt-1">
        <button type="submit" data-testid="contact-submit-button" disabled={state === "sending"} className="inline-flex items-center gap-2 rounded-full bg-moss hover:bg-moss-dark disabled:opacity-70 text-parchment px-7 py-3.5 font-extrabold shadow-journal transition-[background-color,transform] duration-200 hover:-translate-y-0.5">
          {state === "sending" && <Loader2 className="w-4 h-4 animate-spin" />}
          {state === "sending" ? c.sending : c.send}
        </button>
        <p className="text-sm text-ink-muted max-w-xs"><RichText text={c.consent} /></p>
      </div>
    </form>
  );
};

const InfoRow = ({ icon: Icon, label, children, testId }) => (
  <div className="flex gap-3.5" data-testid={testId}>
    <span className="w-9 h-9 rounded-full bg-paper border border-line grid place-items-center shrink-0"><Icon className="w-4 h-4 text-amber" /></span>
    <div className="min-w-0">
      <div className="text-xs font-extrabold uppercase tracking-[0.14em] text-ink-muted">{label}</div>
      <div className="font-bold text-ink break-words">{children}</div>
    </div>
  </div>
);

export default function ContactPage() {
  const { t } = useLang();
  const c = t.pages.contact;
  return (
    <PageShell testId="contact-page">
      <PageHead kicker={c.kicker} title={c.title} lead={c.lead} />
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 mt-14 md:mt-16">
        <div className="lg:col-span-7 journal-card p-6 sm:p-8 md:p-10 relative">
          <ContactForm />
        </div>
        <aside className="lg:col-span-5 space-y-8">
          <div className="journal-card p-6 sm:p-8 space-y-5 bg-paper/70" data-testid="contact-company-card">
            <InfoRow icon={Building2} label={c.company} testId="contact-company-name">{COMPANY.name}</InfoRow>
            <InfoRow icon={Hash} label={c.orgNr} testId="contact-org-number">
              <a href={COMPANY.registryUrl} target="_blank" rel="noopener noreferrer" className="hover:text-moss transition-colors">{COMPANY.orgNr}</a>
            </InfoRow>
            <InfoRow icon={MapPin} label={c.location} testId="contact-location">{c.locationValue}</InfoRow>
            <InfoRow icon={Mail} label={c.emailLabel} testId="contact-email-address">
              <a href={`mailto:${COMPANY.email}`} className="hover:text-moss transition-colors">{COMPANY.email}</a>
            </InfoRow>
          </div>
          <div data-testid="contact-why">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-ink">{c.whyTitle}</h2>
            <ul className="mt-5 space-y-3">
              {c.why.map((item, i) => {
                const Icon = WHY_ICONS[i];
                return (
                  <li key={item} className="flex items-start gap-3 text-ink-muted font-semibold">
                    <Icon className="w-5 h-5 text-moss shrink-0 mt-0.5" />
                    {item}
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>
      </div>
    </PageShell>
  );
}
